import { isAbsoluteURL } from '@lucid/utils';

import useSiteMetadata from './use-site-metadata';
import type { Lucid } from '../types/types';

type ImageMutationOptions = {
  /** The fit of the thumbnail while always preserving the aspect ratio, can be any of the following options:
   * cover — Covers both width/height by cropping/clipping to fit
   * contain — Contain within both width/height using "letterboxing" as needed
   * inside — Resize to be as large as possible, ensuring dimensions are less than or equal to the requested width and height
   * outside — Resize to be as small as possible, ensuring dimensions are greater than or equal to the requested width and height
   */
  fit?: 'cover' | 'contain' | 'inside' | 'outside';
  /** The width of the thumbnail in pixels */
  width?: number;
  /** The height of the thumbnail in pixels */
  height?: number;
  /** The optional quality of the thumbnail (1 to 100) */
  quality?: number;
  /** Disable image up-scaling */
  withoutEnlargement?: boolean;
  /** What file format to return the thumbnail in. One of auto, jpg, png, webp, tiff.
   * auto — Will try to format it in webp or avif if the browser supports it, otherwise it will fallback to jpg.
   */
  format?: 'auto' | 'jpg' | 'png' | 'webp';
  /** any operation found here can be included https://sharp.pixelplumbing.com/api-operation
   * an example: [["blur", 45],["tint", "rgb(255, 0, 0)"],["expand", { "right": 200, "bottom": 150 }]]
   * found from https://docs.directus.io/reference/files.html#advanced
   */
  transforms?: string;
  [key: string]: boolean | number | string | undefined;
};

const HandleImagePath = (media?: string | Lucid.Directus.Generated.Directus_Files, options?: ImageMutationOptions) => {
  const { product } = useSiteMetadata();

  if (!media) return '';

  const mergedOptions = { ...options };
  const optionString = Object.keys(mergedOptions)
    ?.map(key => `${key}=${mergedOptions[key]}`)
    .join('&');

  let fileId = '';

  if (typeof media === 'string') {
    // check if absolute or /static path is passed in and return that value unchanged
    if (isAbsoluteURL(media) || media.startsWith('/static')) return media;
    fileId = media;
  } else {
    fileId = media.filename_disk || '';
  }

  // check if the filename_disk is from /static for build generated files.
  // This will catch external data sources mimicking the proper data structure but that we don't want to change ex: marketplace intergration pages on lucid.co
  if (isAbsoluteURL(fileId) || fileId.startsWith('/static')) return fileId;

  return `https://corporate-assets.lucid.co/${product.replace('blog', '')}/${fileId.replace('*PROD', '')}${optionString ? `?${optionString}` : ''}`;
};

export default HandleImagePath;
